import {
  AGE_BUCKETS,
  type Arrears,
  type MonthTotals,
  type Overview,
  type Period,
  type NetMonth,
  categoryShares,
  finishedAverage,
  foldSmall,
  formatPeriod,
  netByMonth,
  periodOf,
  waterfall,
} from "@liyu/core";
import { esc } from "./dom";
import { card, empty, money, named, percent, sectionTitle } from "./pieces";
import { language, t } from "./strings";
import type { View } from "./views";

export const METRICS = ["collected", "outstanding", "net", "spent", "arrears"] as const;
export type Metric = (typeof METRICS)[number];
const LABELS = {
  collected: "overview.collected",
  outstanding: "analytics.outstanding",
  net: "overview.net",
  spent: "overview.spent",
  arrears: "overview.arrears",
} as const;
const HINTS = {
  collected: "analytics.collectedHint",
  outstanding: "analytics.outstandingHint",
  net: "analytics.netHint",
  spent: "analytics.spentHint",
  arrears: "analytics.arrearsHint",
} as const;
const AGES = {
  current: "analytics.current",
  one: "analytics.one",
  two: "analytics.two",
  threePlus: "analytics.threePlus",
} as const;
/** Lightest for this month, darkest for the oldest debt: the order is the meaning. */
const AGE_TONES = ["age-0", "age-1", "age-2", "age-3"] as const;

export function metricFromRoute(route: string): Metric | undefined {
  return METRICS.find((metric) => route === `analytics-${metric}`);
}

export function metricValue(metric: Metric, sums: Overview): number {
  if (metric === "collected" || metric === "outstanding") return sums.month[metric];
  return sums[metric];
}

export function metricCards(sums: Overview): string {
  return `<div class="metric-grid">${METRICS.map(
    (metric) =>
      `<button type="button" class="card metric-card metric-${metric}" data-metric="${metric}"><span class="metric-label">${esc(t(LABELS[metric]))}</span><strong class="stat-value">${esc(signed(metricValue(metric, sums)))}</strong><span class="metric-link">${esc(t("analytics.open"))}<span aria-hidden="true">↗</span></span></button>`,
  ).join("")}</div>`;
}

export function wireMetrics(root: HTMLElement, go: (view: string) => void): void {
  for (const button of root.querySelectorAll<HTMLButtonElement>("[data-metric]")) {
    button.addEventListener("click", () => {
      go(`analytics-${button.dataset.metric}`);
      const heading = document.getElementById("pageTitle");
      heading?.setAttribute("tabindex", "-1");
      heading?.focus();
    });
  }
}

interface ExpenseRow {
  category: string;
  amount: number;
  period: Period;
  voided: boolean;
}

export function expenseBreakdown(expenses: readonly ExpenseRow[], period: Period) {
  const groups = new Map<string, number>();
  for (const expense of expenses) {
    if (expense.voided || expense.period !== period) continue;
    groups.set(expense.category, (groups.get(expense.category) ?? 0) + expense.amount);
  }
  return [...groups.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

/** A loss written "−ETB 1,300", the sign in front of the currency as it is said. */
function signed(amount: number): string {
  return amount < 0 ? `−${money(-amount)}` : money(amount);
}

/** A figure over a column, with the currency on its own line where columns are narrow. */
function figure(text: string): string {
  return esc(text).replace(/^(−?ETB) /, '<span class="cur">$1 </span>');
}

function monthName(period: Period): string {
  return formatPeriod(period, language() === "am" ? "am" : "latin");
}

/** "Ginbot 2018" as a label and a smaller line under it, so six fit across a phone. */
function twoLines(period: Period): [string, string] {
  const [first, ...rest] = monthName(period).split(" ");
  return [first ?? "", rest.join(" ")];
}

function legend(items: [string, string][]): string {
  return `<ul class="chart-legend">${items
    .map(
      ([tone, label]) =>
        `<li><span class="chart-key ${tone}" aria-hidden="true"></span>${esc(label)}</li>`,
    )
    .join("")}</ul>`;
}

/**
 * One column per month: the whole column is what was billed and the lower part
 * what was verified. The part the screen is about is drawn solid and the other
 * is a wash, so both figures read off one picture. The month still being paid
 * is marked as such, so it reads as unfinished rather than as a slump.
 */
function columns(trend: readonly MonthTotals[], focus: "collected" | "outstanding"): string {
  const top = Math.max(1, ...trend.map((month) => month.billed));
  const now = periodOf(new Date());
  // A line to read the columns against, as Treasury's revenue chart reads this
  // year against an average. Only for what came in: an average of debt is not
  // a level anybody is trying to reach.
  const average = focus === "collected" ? finishedAverage(trend, now) : null;
  const cols = trend
    .map((month) => {
      const line = t("analytics.columnLine", {
        month: monthName(month.period),
        billed: money(month.billed),
        verified: money(month.collected),
        rate: percent(month.rate),
      });
      const [first, rest] = twoLines(month.period);
      const live = month.period === now;
      const shown = focus === "collected" ? percent(month.rate) : money(month.outstanding);
      return `<li class="col${live ? " col-live" : ""}" title="${esc(line)}"><span class="col-figure" aria-hidden="true">${figure(shown)}</span><span class="col-plot" aria-hidden="true"><span class="col-stack" style="height:${String((month.billed / top) * 100)}%"><span class="seg seg-out" style="flex:${String(month.outstanding)}"></span><span class="seg seg-in" style="flex:${String(month.collected)}"></span></span></span><span class="col-label" aria-hidden="true">${esc(first)}</span><span class="col-sub" aria-hidden="true">${esc(live ? t("analytics.soFar") : rest)}</span><span class="sr-only">${esc(line)}</span></li>`;
    })
    .join("");
  const keys: [string, string][] =
    focus === "collected"
      ? [
          ["key-in", t("analytics.verified")],
          ["key-out", t("analytics.unpaid")],
        ]
      : [
          ["key-out", t("analytics.unpaid")],
          ["key-in", t("analytics.verified")],
        ];
  if (average !== null)
    keys.push(["key-average", t("analytics.average", { amount: money(average) })]);
  const line =
    average === null ? "" : ` has-average" style="--average:${String((average / top) * 100)}%`;
  // Said under every column chart, because a payment waiting on the accountant
  // is drawn as outstanding, and a resident who has paid should not read as behind.
  return (
    legend(keys) +
    `<ol class="chart-columns focus-${focus}${line}">${cols}</ol><p class="hint chart-note">${esc(t("analytics.outstandingNote"))}</p>`
  );
}

/** The exact figures behind a chart, folded away until somebody wants them. */
function figures(head: string[], rows: string[][]): string {
  return `<details class="chart-figures"><summary>${esc(t("analytics.figures"))}</summary><table class="table"><thead><tr>${head
    .map((cell) => `<th scope="col">${esc(cell)}</th>`)
    .join("")}</tr></thead><tbody>${rows
    .map(
      (row) =>
        `<tr>${row
          .map((cell, index) =>
            index === 0 ? `<th scope="row">${esc(cell)}</th>` : `<td>${esc(cell)}</td>`,
          )
          .join("")}</tr>`,
    )
    .join("")}</tbody></table></details>`;
}

function monthTable(trend: readonly MonthTotals[]): string {
  return figures(
    [
      t("analytics.month"),
      t("analytics.billed"),
      t("analytics.verified"),
      t("analytics.unpaid"),
      t("analytics.rate"),
    ],
    trend.map((month) => [
      monthName(month.period),
      money(month.billed),
      money(month.collected),
      money(month.outstanding),
      percent(month.rate),
    ]),
  );
}

/** A thin bar per block, verified then outstanding, worst block first. */
function blockRows(sums: Overview): string {
  return `<ul class="chart-rows">${sums.blocks
    .filter((block) => block.billed > 0)
    .map(
      (block) =>
        `<li><span class="row-name">${esc(t("units.block", { block: block.block }))}</span><span class="row-bar" aria-hidden="true"><span class="seg seg-in" style="flex:${String(block.collected)}"></span><span class="seg seg-out" style="flex:${String(block.outstanding)}"></span></span><strong>${esc(money(block.outstanding))}</strong><span class="row-note">${esc(t("analytics.blockLine", { percent: percent(block.rate) }))}</span></li>`,
    )
    .join("")}</ul>`;
}

interface Floating {
  label: string;
  from: number;
  to: number;
  figure: string;
  tone: string;
  /** Where the next bar starts, drawn as a connector so the eye can follow the running total. */
  link?: number | undefined;
}

/**
 * A waterfall, drawn to Datawrapper's rules for one: red for what went out,
 * one neutral colour for the totals, every bar labelled, a connector from each
 * bar to the next where the running total carries on. It runs top to bottom
 * rather than left to right, which is the same guide's answer to labels that
 * will not fit under narrow columns: a category name reads whole on its row.
 */
function floating(bars: Floating[]): string {
  const low = Math.min(0, ...bars.map((bar) => bar.from));
  const high = Math.max(1, ...bars.map((bar) => bar.to));
  const at = (value: number) => ((value - low) / (high - low)) * 100;
  return `<ol class="waterfall" style="--zero:${String(at(0))}%">${bars
    .map(
      (bar) =>
        `<li class="${bar.tone}"><span class="row-name">${esc(bar.label)}</span><span class="fall-track" aria-hidden="true"><span class="fall-bar" style="left:${String(at(bar.from))}%;width:${String(Math.max(at(bar.to) - at(bar.from), 0.6))}%"></span>${bar.link === undefined ? "" : `<span class="fall-link" style="left:${String(at(bar.link))}%"></span>`}</span><strong>${esc(bar.figure)}</strong></li>`,
    )
    .join("")}</ol>`;
}

/**
 * Money in and money out as two dots on one line per month, the gap between
 * them coloured by which way the month went. Read across, as Treasury reads
 * revenue against spending: the length of the gap is the surplus or the
 * shortfall, and a month nobody has finished paying is marked as such.
 */
function dumbbell(months: readonly NetMonth[]): string {
  const top = Math.max(1, ...months.flatMap((month) => [month.collected, month.spent]));
  const at = (value: number) => (value / top) * 100;
  const now = periodOf(new Date());
  return `<ul class="dumbbell">${months
    .map((month) => {
      const [label, year] = twoLines(month.period);
      const low = Math.min(month.collected, month.spent);
      const high = Math.max(month.collected, month.spent);
      const tone = month.net < 0 ? "gap-loss" : "gap-gain";
      return `<li><span class="row-name">${esc(label)} <small>${esc(month.period === now ? t("analytics.soFar") : year)}</small></span><span class="db-track" aria-hidden="true"><span class="db-gap ${tone}" style="left:${String(at(low))}%;width:${String(at(high) - at(low))}%"></span><span class="db-dot db-in" style="left:${String(at(month.collected))}%"></span><span class="db-dot db-out" style="left:${String(at(month.spent))}%"></span></span><strong class="${month.net < 0 ? "is-loss" : ""}">${esc(signed(month.net))}</strong></li>`;
    })
    .join("")}</ul>`;
}

interface Ranked {
  tone: string;
  amount: number;
  name: string;
  figure: string;
  note: string;
}

/**
 * Bars on one shared baseline, longest to shortest where order is free and in
 * their own order where it is not. Datawrapper's advice for parts of a whole
 * whose total can be read elsewhere: lengths from one baseline compare far
 * better than segments of a single stacked bar.
 */
function rankBars(rows: Ranked[]): string {
  const top = Math.max(1, ...rows.map((row) => row.amount));
  return `<ul class="rank-bars">${rows
    .map(
      (row) =>
        `<li><span class="row-name">${esc(row.name)}</span><span class="rank-track" aria-hidden="true"><span class="${row.tone}" style="width:${String(Math.max((row.amount / top) * 100, row.amount > 0 ? 1 : 0))}%"></span></span><strong>${esc(row.figure)}</strong><span class="row-note">${esc(row.note)}</span></li>`,
    )
    .join("")}</ul>`;
}

function change(now: number, before: number): string {
  if (before === 0) return t("analytics.new");
  if (now === before) return t("analytics.same");
  return now > before
    ? t("analytics.more", { amount: money(now - before) })
    : t("analytics.less", { amount: money(before - now) });
}

function overMonths(metric: "collected" | "outstanding", sums: Overview): string {
  const trend = sums.trend.filter((month) => month.billed > 0);
  if (trend.length === 0) return empty(t("analytics.empty"));
  let html =
    sectionTitle(
      t(metric === "collected" ? "analytics.billedVerified" : "analytics.billedOutstanding"),
    ) + card(columns(trend, metric) + monthTable(trend), `analytics-chart metric-${metric}`);
  if (metric === "outstanding" && sums.blocks.some((block) => block.billed > 0)) {
    html +=
      sectionTitle(t("analytics.byBlock")) +
      card(blockRows(sums), "analytics-chart metric-outstanding");
  }
  return html;
}

function inAndOut(sums: Overview, expenses: readonly ExpenseRow[]): string {
  const shares = foldSmall(categoryShares(expenses, sums.month.period));
  if (sums.month.collected === 0 && shares.length === 0) return empty(t("analytics.empty"));
  const steps = waterfall(sums.month.collected, shares).map((step): Floating => ({
    label:
      step.kind === "in"
        ? t("analytics.stepIn")
        : step.kind === "net"
          ? t(step.amount < 0 ? "analytics.stepShort" : "analytics.stepNet")
          : named("category", step.category),
    from: step.from,
    to: step.to,
    figure: step.kind === "out" ? `−${money(step.amount)}` : signed(step.amount),
    tone:
      step.kind === "out"
        ? "tone-out"
        : `tone-total${step.kind === "net" && step.amount < 0 ? " is-loss" : ""}`,
    link: step.kind === "in" ? step.to : step.kind === "out" ? step.from : undefined,
  }));
  let html =
    sectionTitle(t("analytics.whereWent")) + card(floating(steps), "analytics-chart metric-net");
  const months = netByMonth(sums.trend, expenses);
  if (months.length > 1) {
    html +=
      sectionTitle(t("analytics.netMonths")) +
      card(
        legend([
          ["key-dot-in", t("analytics.stepIn")],
          ["key-dot-out", t("analytics.spentCol")],
          ["key-gain", t("analytics.surplus")],
          ["key-loss", t("analytics.loss")],
        ]) +
          dumbbell(months) +
          figures(
            [
              t("analytics.month"),
              t("analytics.verified"),
              t("analytics.spentCol"),
              t("analytics.netCol"),
            ],
            months.map((month) => [
              monthName(month.period),
              money(month.collected),
              money(month.spent),
              signed(month.net),
            ]),
          ),
        "analytics-chart metric-net",
      );
  }
  return html;
}

function spentOn(sums: Overview, expenses: readonly ExpenseRow[]): string {
  const shares = foldSmall(categoryShares(expenses, sums.month.period));
  if (shares.length === 0) return empty(t("analytics.empty"));
  return (
    sectionTitle(t("analytics.whatOn")) +
    card(
      rankBars(
        shares.map((share) => ({
          tone: "tone-spend",
          amount: share.amount,
          name: named("category", share.category),
          figure: money(share.amount),
          note: `${percent(share.share)} · ${change(share.amount, share.before)}`,
        })),
      ),
      "analytics-chart metric-spent",
    )
  );
}

function howLate(arrears: Arrears): string {
  if (arrears.owed === 0) return empty(t("analytics.empty"));
  const ages = AGE_BUCKETS.map((bucket, index): Ranked => ({
    tone: AGE_TONES[index] ?? "age-3",
    amount: arrears.buckets[bucket].owed,
    name: t(AGES[bucket]),
    figure: money(arrears.buckets[bucket].owed),
    note: t("analytics.unitsCount", { count: arrears.buckets[bucket].units }),
  }));
  const worst = arrears.rows
    .slice(0, 5)
    .map(
      (row) =>
        `<li><span class="row-name">${esc(row.unitId)}</span><span class="row-note">${esc(t("analytics.monthsOwed", { count: row.months }))}</span><strong>${esc(money(row.owed))}</strong></li>`,
    )
    .join("");
  return (
    sectionTitle(t("analytics.howLate")) +
    card(rankBars(ages), "analytics-chart metric-arrears") +
    sectionTitle(t("analytics.owingMost")) +
    card(`<ul class="chart-rows owing-rows">${worst}</ul>`, "analytics-chart metric-arrears")
  );
}

export function analyticsView(
  metric: Metric,
  sums: Overview,
  arrears: Arrears,
  expenses: readonly ExpenseRow[],
): View {
  const body =
    metric === "collected" || metric === "outstanding"
      ? overMonths(metric, sums)
      : metric === "net"
        ? inAndOut(sums, expenses)
        : metric === "spent"
          ? spentOn(sums, expenses)
          : howLate(arrears);
  return {
    title: t(LABELS[metric]),
    sub: metric === "arrears" ? t("analytics.allMonths") : monthName(sums.month.period),
    html:
      `<button type="button" id="analyticsBack" class="btn btn-ghost">← ${esc(t("nav.overview"))}</button><nav class="metric-tabs" aria-label="${esc(t("analytics.choose"))}">${METRICS.map(
        (choice) =>
          `<button type="button" class="btn btn-ghost" data-metric="${choice}" aria-current="${choice === metric ? "page" : "false"}">${esc(t(LABELS[choice]))}</button>`,
      ).join("")}</nav>` +
      card(
        `<h2 class="metric-label">${esc(t(LABELS[metric]))}</h2><div class="stat-value">${esc(signed(metricValue(metric, sums)))}</div><p class="hint">${esc(t(HINTS[metric]))}</p>`,
        `analytics-summary metric-${metric}`,
      ) +
      body,
    wire(root, go) {
      wireMetrics(root, go);
      root.querySelector("#analyticsBack")?.addEventListener("click", () => {
        go("home");
        root.querySelector<HTMLButtonElement>(`[data-metric="${metric}"]`)?.focus();
      });
    },
  };
}
